import { suggestFieldBoundaryFromCoverage } from 'app/utils/field-boundary-suggestion.utils';
import { errorNameOf } from 'app/utils/field-boundary/shared';
import { collectCoveragePolygons } from 'app/utils/field-boundary/task-data';
import {
  DeviceBoundary,
  FieldBoundarySuggestion,
  FieldBoundarySuggestionTask,
  LatLngPoint,
} from 'app/utils/field-boundary/types';

const COORDINATES_PER_POINT = 2;

/**
 * Worked polygons flattened into transferable buffers. Posting a large task's
 * polygons as objects makes the main thread clone them for hundreds of
 * milliseconds; transferring these buffers copies nothing.
 */
export interface PackedCoverage {
  /** [lng, lat] of every point, polygon after polygon. */
  coordinates: Float64Array;
  /** First point of each polygon, plus one past the last point. */
  offsets: Int32Array;
}

export interface FieldBoundaryWorkerRequest {
  boundaries: DeviceBoundary[];
  coverage: PackedCoverage;
  implementWidthMeters: number | null;
}

export type FieldBoundaryWorkerResponse =
  | { suggestion: FieldBoundarySuggestion | null }
  | { errorName: string };

export function packCoverage(polygons: LatLngPoint[][]): PackedCoverage {
  const offsets = new Int32Array(polygons.length + 1);
  let pointCount = 0;
  polygons.forEach((path, index) => {
    offsets[index] = pointCount;
    pointCount += path.length;
  });
  offsets[polygons.length] = pointCount;

  const coordinates = new Float64Array(pointCount * COORDINATES_PER_POINT);
  let position = 0;
  polygons.forEach((path) =>
    path.forEach((point) => {
      coordinates[position++] = point?.lng;
      coordinates[position++] = point?.lat;
    }),
  );
  return { coordinates, offsets };
}

export function unpackCoverage({
  coordinates,
  offsets,
}: PackedCoverage): LatLngPoint[][] {
  const polygons: LatLngPoint[][] = [];
  for (let index = 0; index < offsets.length - 1; index++) {
    const path: LatLngPoint[] = [];
    for (let point = offsets[index]; point < offsets[index + 1]; point++) {
      path.push({
        lng: coordinates[point * COORDINATES_PER_POINT],
        lat: coordinates[point * COORDINATES_PER_POINT + 1],
      });
    }
    polygons.push(path);
  }
  return polygons;
}

/**
 * Builds the worker request on the main thread: only the worked polygons are
 * kept, and only the boundary paths, so nothing else of the task is copied.
 */
export function createFieldBoundaryWorkerRequest(
  task: FieldBoundarySuggestionTask,
): FieldBoundaryWorkerRequest {
  let boundaries: DeviceBoundary[] = [];
  if (Array.isArray(task.boundaries)) {
    boundaries = task.boundaries.map((boundary) => ({ path: boundary?.path }));
  }
  return {
    boundaries,
    coverage: packCoverage(collectCoveragePolygons(task.trails)),
    implementWidthMeters: task.implementWidthMeters ?? null,
  };
}

/** Runs a request, reporting failures by error name only, never by message. */
export function handleFieldBoundaryWorkerRequest(
  request: FieldBoundaryWorkerRequest,
): FieldBoundaryWorkerResponse {
  try {
    return {
      suggestion: suggestFieldBoundaryFromCoverage({
        boundaries: request.boundaries,
        coveragePolygons: unpackCoverage(request.coverage),
        implementWidthMeters: request.implementWidthMeters,
      }),
    };
  } catch (error) {
    return { errorName: errorNameOf(error) };
  }
}
